(ns app.external-module.model
  "External modules: products of a tenant that another system implements,
  PWAM first. Each is a `:group.type/external-module` group under the
  domain group, naming the system (`:group/external-system`) and module
  (`:group/external-module-id`), with user-groups tagged by
  `:group/external-kind`: role groups (at most one per user and module) and
  department groups (any number). They grant nothing here; membership is the
  assignment in the other system, and is pushed there."
  (:require [app.domain.group.model :as group-model]
            [clojure.string :as str]))

(def systems #{:external-system/pwam})

(def kinds #{:external-group.kind/role :external-group.kind/department})

;; Absent = never sent to the other system.
(def sync-statuses #{:external-sync-status/synced :external-sync-status/pending})

(def default-departments
  "Department names offered when a tenant enables its first departments, per
  system. A tenant may add any other name; these are only a starting point."
  {:external-system/pwam ["Public Works"
                          "Utilities"
                          "Community Services"
                          "Streets & Roads"
                          "Sidewalks & Pathways"
                          "Bridges & Structures"
                          "Traffic & Signals"
                          "Street Lighting"
                          "Transit"
                          "Airport"
                          "Water / Water Distribution"
                          "Sewer / Wastewater"
                          "Stormwater / Drainage"
                          "Parks & Recreation"
                          "Sportsfields & Athletics"
                          "Trails & Open Space"
                          "Urban Forestry / Trees"
                          "Aquatics / Pools"
                          "Facilities & Buildings"
                          "Building Maintenance"
                          "Custodial / Janitorial"
                          "Fleet / Vehicle Maintenance"
                          "Equipment"
                          "Solid Waste / Sanitation"
                          "Recycling"
                          "Electric / Public Power"
                          "Gas"
                          "Engineering"]})

;; ---------------------------------------------------------------------------
;; Predicates
;; ---------------------------------------------------------------------------

(def external-module? group-model/external-module?)

(defn role-group? [group]
  (= :external-group.kind/role (:group/external-kind group)))

(defn department-group? [group]
  (= :external-group.kind/department (:group/external-kind group)))

(defn external-user-group?
  "A user-group of an external module: a role or a department group."
  [group]
  (contains? kinds (:group/external-kind group)))

(defn sent?
  "Whether the group has been pushed to the other system at least once. Once it
  has, it may have been attributed data there, so it is never removed."
  [group]
  (some? (:group/external-sync-status group)))

(defn pending? [group]
  (= :external-sync-status/pending (:group/external-sync-status group)))

(defn enabled?
  "An external-module group that has been disabled keeps its groups but is not
  an active product."
  [module-group]
  (not (:group/external-disabled? module-group)))

;; ---------------------------------------------------------------------------
;; Traversal
;; ---------------------------------------------------------------------------

(defn module-groups
  "The external-module groups of a domain group, optionally of one system."
  ([domain-group]
   (filter external-module? (group-model/child-groups domain-group)))
  ([domain-group system]
   (filter (comp #{system} :group/external-system) (module-groups domain-group))))

(defn module-group-of
  "The domain group's module group for `system` and `module-id`, if any."
  [domain-group system module-id]
  (->> (module-groups domain-group system)
       (filter (comp #{module-id} :group/external-module-id))
       first))

(defn role-groups [module-group]
  (->> (group-model/child-groups module-group)
       (filter role-group?)
       (sort-by :group/name)))

(defn department-groups [module-group]
  (->> (group-model/child-groups module-group)
       (filter department-group?)
       (sort-by :group/name)))

(defn user-role-group
  "The role group of `module-group` the user is in, if any."
  [module-group user]
  (let [user-group-ids (->> (:user/groups user) (map :db/id) set)]
    (->> (role-groups module-group)
         (filter (comp user-group-ids :db/id))
         first)))

(defn user-department-groups [module-group user]
  (let [user-group-ids (->> (:user/groups user) (map :db/id) set)]
    (->> (department-groups module-group)
         (filter (comp user-group-ids :db/id)))))

;; ---------------------------------------------------------------------------
;; Identifiers, built like authority group ids: `CA-3539033-PWAM`, with
;; `...-ROLE-2501` and `...-DEPT-PUBLIC-WORKS` under it. The other system is told
;; the module group's id, so it must stay stable.
;; ---------------------------------------------------------------------------

(defn module-group-id [domain-group-id module-key]
  (str domain-group-id "-" module-key))

(defn module-group-name
  "Named as an authority group is, e.g. \"Windsor, Public Works Asset Management\"."
  [domain-group-name module-name]
  (str/join ", " [domain-group-name module-name]))

(defn role-group-id [module-group-id role-id]
  (str module-group-id "-ROLE-" role-id))

(defn department-slug
  "A department name reduced to the characters a group id can carry:
  \"Water / Water Distribution\" -> \"WATER-WATER-DISTRIBUTION\"."
  [department-name]
  (-> department-name
      str/trim
      str/upper-case
      (str/replace #"[^A-Z0-9]+" "-")
      (str/replace #"^-+|-+$" "")))

(defn department-group-id [module-group-id department-name]
  (str module-group-id "-DEPT-" (department-slug department-name)))

(defn department-code
  "The department's stable code, which is what the other system knows it by:
  the slug its `:group/id` was built from, so it survives a change of name."
  [{:keys [entity/parent] :as department-group}]
  (let [prefix (str (:group/id parent) "-DEPT-")
        id     (:group/id department-group)]
    (when (str/starts-with? id prefix)
      (subs id (count prefix)))))

(def department-max-length
  "The longest department name and code each system accepts. PWAM limits
  both to 100 characters (`CloudassetDepartment.displayName`, `.departmentCode`)."
  {:external-system/pwam 100})

(defn department-name-problem
  "Why `department-name` cannot be a department of `system`, or nil: `:invalid`
  when its code is empty, `:too-long` when the name or the code (upper-casing
  can lengthen it) exceeds the system's limit."
  [system department-name]
  (let [code  (department-slug department-name)
        limit (get department-max-length system)]
    (cond
      (str/blank? code)                                                :invalid
      (and limit (> (max (count department-name) (count code)) limit)) :too-long)))

(defn normalize-department-name [department-name]
  (-> department-name str/trim (str/replace #"\s+" " ")))
